import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Progress, Button, Typography, Space, Tag, Empty, Statistic, Divider, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined, InfoCircleOutlined, BulbOutlined, ArrowUpOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from '../../../dayjs';

const { Text, Title, Paragraph } = Typography;

/**
 * TrackedDailyOverviewTab component displays a date carousel with radial pie charts
 * showing tracking coverage by date, focusing on data availability rather than timing performance.
 */
const TrackedDailyOverviewTab = ({ data, selectedStopTypes, onStopTypeChange }) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [visibleDates, setVisibleDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [datePerformance, setDatePerformance] = useState({});
  const [dailyInsights, setDailyInsights] = useState(null);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);

  // Helper function to determine if a stop is tracked
  const isStopTracked = (record) => {
    const hasActualArrival = record.ACTUAL_ARRIVAL_TIME !== undefined && record.ACTUAL_ARRIVAL_TIME !== null;
    const hasActualDeparture = record.ACTUAL_DEPARTURE_TIME !== undefined && record.ACTUAL_DEPARTURE_TIME !== null;
    const hasCompactActualArrival = record.A_ARR !== undefined && record.A_ARR !== null;
    const hasCompactActualDeparture = record.A_DEP !== undefined && record.A_DEP !== null;
    
    return hasActualArrival || hasActualDeparture || hasCompactActualArrival || hasCompactActualDeparture;
  };

  // Generate dates for the current month
  useEffect(() => {
    const dates = [];
    const startDate = currentMonth.startOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    
    for (let i = 0; i < daysInMonth; i++) {
      dates.push(startDate.clone().add(i, 'day'));
    }
    
    setVisibleDates(dates);
  }, [currentMonth]);
  
  // Calculate tracking coverage by date using the data
  useEffect(() => {
    if (!data || data.length === 0) {
      setDatePerformance({});
      return;
    }
    
    // Group data by date
    calculateTrackingByDate(data);
  }, [data, selectedStopTypes]);
  
  // Find and select the most recent date with data
  useEffect(() => {
    if (!initialSelectionDone && Object.keys(datePerformance).length > 0) {
      // Get all dates with data
      const datesWithData = Object.keys(datePerformance)
        .filter(dateStr => {
          const perfData = datePerformance[dateStr];
          // Check that we have actual data in this date entry
          return perfData && (
            (perfData.totalCount && perfData.totalCount > 0) || 
            (perfData.firstStops && perfData.firstStops.total && perfData.firstStops.total > 0) ||
            (perfData.midStops && perfData.midStops.total && perfData.midStops.total > 0) ||
            (perfData.lastStops && perfData.lastStops.total && perfData.lastStops.total > 0)
          );
        })
        .map(dateStr => {
          // Handle different date formats
          if (dateStr.includes('-')) {
            return dayjs(dateStr);
          } else {
            return dayjs(dateStr, 'YYYYMMDD');
          }
        })
        .sort((a, b) => b.valueOf() - a.valueOf()); // Sort by date, newest first
      
      console.log('Found dates with data:', datesWithData.map(d => d.format('YYYY-MM-DD')));
      
      // If we have dates with data, select the most recent one
      if (datesWithData.length > 0) {
        const mostRecentDate = datesWithData[0];
        console.log('Selecting most recent date with data:', mostRecentDate.format('YYYY-MM-DD'));
        
        setSelectedDate(mostRecentDate);
        
        // If the most recent date is not in the current month view, update the month
        if (mostRecentDate.month() !== currentMonth.month() || 
            mostRecentDate.year() !== currentMonth.year()) {
          setCurrentMonth(mostRecentDate);
        }
        
        // Generate insights for this date (simplified for tracking)
        const dateStr = mostRecentDate.format('YYYYMMDD');
        const dateData = data.filter(record => 
          String(record.DATE_ID) === dateStr ||
          record.dateStr === dateStr
        );
        
        const insights = generateTrackingInsights(dateStr, dateData);
        setDailyInsights(insights);
      }
      
      setInitialSelectionDone(true);
    }
  }, [datePerformance, data, currentMonth, initialSelectionDone]);
  
  // Generate tracking insights for a date
  const generateTrackingInsights = (dateStr, dateData) => {
    if (!dateData || dateData.length === 0) return null;
    
    const totalStops = dateData.length;
    const trackedStops = dateData.filter(record => isStopTracked(record)).length;
    const untrackedStops = totalStops - trackedStops;
    const trackingPercent = Math.round((trackedStops / totalStops) * 100);
    
    // Analyze data availability by type
    const departureDataCount = dateData.filter(record => 
      Number.isFinite(record.ACTUAL_DEPARTURE_TIME) || Number.isFinite(record.A_DEP)
    ).length;
    const arrivalDataCount = dateData.filter(record => 
      Number.isFinite(record.ACTUAL_ARRIVAL_TIME) || Number.isFinite(record.A_ARR)
    ).length;
    
    // Analyze by stop type
    const firstStops = dateData.filter(record => record.TSN_FIRST_LAST_IND === 'F');
    const midStops = dateData.filter(record => record.TSN_FIRST_LAST_IND === 'M');
    const lastStops = dateData.filter(record => record.TSN_FIRST_LAST_IND === 'L');
    
    const firstTracked = firstStops.filter(record => isStopTracked(record)).length;
    const midTracked = midStops.filter(record => isStopTracked(record)).length;
    const lastTracked = lastStops.filter(record => isStopTracked(record)).length;
    
    let insight = null;
    let recommendation = null;
    let improvement = 0;
    let category = 'tracking';
    let priority = 'medium';
    
    // Detailed analysis based on tracking coverage
    if (trackingPercent < 30) {
      insight = `Critical tracking coverage issue: Only ${trackingPercent}% of ${totalStops} stops have actual timing data. ${untrackedStops} stops are completely untracked.`;
      recommendation = `Immediate action required: Check GPS devices, verify data transmission systems, and ensure all vehicles have functioning tracking equipment.`;
      improvement = Math.min(70 - trackingPercent, 40);
      priority = 'critical';
    } else if (trackingPercent < 50) {
      insight = `Poor tracking coverage: ${trackingPercent}% coverage with ${untrackedStops} untracked stops out of ${totalStops} total. Data quality is insufficient for reliable analysis.`;
      recommendation = `Prioritize fixing tracking systems. Focus on routes with consistently missing data and verify GPS signal strength in problem areas.`;
      improvement = Math.min(70 - trackingPercent, 30);
      priority = 'low';
    } else if (trackingPercent < 70) {
      insight = `Below-average tracking coverage: ${trackingPercent}% of stops tracked. ${untrackedStops} stops still missing timing data.`;
      
      // Provide specific recommendations based on data patterns
      if (departureDataCount > arrivalDataCount) {
        recommendation = `Arrival timing data is weaker than departure data. Focus on improving arrival time recording systems and GPS accuracy at destination points.`;
      } else if (arrivalDataCount > departureDataCount) {
        recommendation = `Departure timing data needs improvement. Check GPS initialization at route start points and ensure proper system startup procedures.`;
      } else {
        recommendation = `Review data collection processes systematically. Identify specific routes or time periods with consistent tracking gaps.`;
      }
      
      improvement = Math.min(85 - trackingPercent, 25);
      priority = 'low';
    } else if (trackingPercent < 85) {
      insight = `Good tracking coverage: ${trackingPercent}% of stops have timing data. ${untrackedStops} stops could still be improved.`;
      
      // Analyze stop type performance
      const worstStopType = firstStops.length > 0 && (firstTracked / firstStops.length) < 0.8 ? 'first stops' :
                           midStops.length > 0 && (midTracked / midStops.length) < 0.8 ? 'mid stops' :
                           lastStops.length > 0 && (lastTracked / lastStops.length) < 0.8 ? 'last stops' : null;
      
      if (worstStopType) {
        recommendation = `Focus on improving tracking for ${worstStopType}. These may have specific technical challenges that need addressing.`;
      } else {
        recommendation = `Fine-tune remaining data collection gaps. Consider implementing backup tracking methods for edge cases.`;
      }
      
      improvement = Math.min(95 - trackingPercent, 15);
      priority = 'medium';
    } else if (trackingPercent < 95) {
      insight = `Very good tracking coverage: ${trackingPercent}% of stops tracked successfully. Only ${untrackedStops} stops missing data.`;
      recommendation = `Excellent performance! Focus on identifying and resolving the few remaining edge cases to achieve near-perfect coverage.`;
      improvement = Math.min(98 - trackingPercent, 8);
      priority = 'high';
    } else {
      insight = `Excellent tracking coverage: ${trackingPercent}% coverage achieved. Outstanding data collection quality.`;
      recommendation = `Maintain current systems and processes. Monitor for any degradation in coverage quality.`;
      improvement = 0;
      priority = 'high';
    }
    
    return insight ? {
      insight,
      recommendation,
      improvement,
      category,
      priority,
      metrics: {
        totalStops,
        trackedStops,
        untrackedStops,
        trackingPercent,
        departureDataCount,
        arrivalDataCount,
        stopTypeBreakdown: {
          first: { total: firstStops.length, tracked: firstTracked },
          mid: { total: midStops.length, tracked: midTracked },
          last: { total: lastStops.length, tracked: lastTracked }
        }
      }
    } : null;
  };
  
  // Calculate tracking coverage for each date
  const calculateTrackingByDate = (rawData) => {
    if (!rawData || rawData.length === 0) {
      setDatePerformance({});
      return;
    }
    
    // First group all records by date
    const recordsByDate = {};
    
    // Group all records by date
    rawData.forEach(record => {
      const dateId = record.DATE_ID;
      if (!dateId) return;
      
      // Use String(dateId) directly instead of formatted date
      const dateStr = String(dateId);
      
      if (!recordsByDate[dateStr]) {
        recordsByDate[dateStr] = [];
      }
      
      recordsByDate[dateStr].push(record);
    });
    
    // Calculate tracking coverage for each date
    const performanceByDate = {};
    
    // Process each date's data separately
    Object.keys(recordsByDate).forEach(dateStr => {
      const dateRecords = recordsByDate[dateStr];
      
      // Calculate tracking coverage for each date
      if (!performanceByDate[dateStr]) {
        performanceByDate[dateStr] = {
          trackedStops: 0,
          untrackedStops: 0,
          totalStops: 0,
          firstStops: { tracked: 0, untracked: 0, total: 0 },
          midStops: { tracked: 0, untracked: 0, total: 0 },
          lastStops: { tracked: 0, untracked: 0, total: 0 }
        };
      }
      
      // Process all records for statistics regardless of stop type
      dateRecords.forEach(record => {
        const tracked = isStopTracked(record);
        
        // Categorize by stop type - we'll store all stats regardless of current filter
        if (record.TSN_FIRST_LAST_IND === 'F') {
          performanceByDate[dateStr].firstStops.total++;
          if (tracked) {
            performanceByDate[dateStr].firstStops.tracked++;
          } else {
            performanceByDate[dateStr].firstStops.untracked++;
          }
        } else if (record.TSN_FIRST_LAST_IND === 'M') {
          performanceByDate[dateStr].midStops.total++;
          if (tracked) {
            performanceByDate[dateStr].midStops.tracked++;
          } else {
            performanceByDate[dateStr].midStops.untracked++;
          }
        } else if (record.TSN_FIRST_LAST_IND === 'L') {
          performanceByDate[dateStr].lastStops.total++;
          if (tracked) {
            performanceByDate[dateStr].lastStops.tracked++;
          } else {
            performanceByDate[dateStr].lastStops.untracked++;
          }
        }
      });
      
      // Now calculate the overall stats based on selectedStopTypes for this date
      const filteredRecords = dateRecords.filter(record => {
        // Check if the record has a stop type that matches our filters
        if (selectedStopTypes.includes('ALL')) return true;
        return selectedStopTypes.includes(record.TSN_FIRST_LAST_IND);
      });
      
      // Count tracked stops
      const trackedStops = filteredRecords.filter(record => isStopTracked(record));
      
      // Calculate tracking percentage using all filtered records
      let trackedCount = trackedStops.length;
      let totalCount = filteredRecords.length;
      
      // Calculate tracking percentage
      const trackingPercent = totalCount > 0 ? Math.round((trackedCount / totalCount) * 100) : 0;
      
      // Store in state
      performanceByDate[dateStr] = {
        dateStr,
        trackingPercent,
        trackedCount,
        totalCount,
        untrackedCount: totalCount - trackedCount,
        firstStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'F').length,
          tracked: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'F' && isStopTracked(r)).length
        },
        midStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'M').length,
          tracked: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'M' && isStopTracked(r)).length
        },
        lastStops: {
          total: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'L').length,
          tracked: filteredRecords.filter(r => r.TSN_FIRST_LAST_IND === 'L' && isStopTracked(r)).length
        }
      };
    });
    
    setDatePerformance(performanceByDate);
  };
  
  // Calculate tracking coverage for a specific date based on selected stop types
  const calculateTrackingForDate = (date) => {
    // First try to match the exact format used in datePerformance
    const formattedDate = date.format('YYYYMMDD');
    // Check all possible date formats to find a match
    const potentialKeys = [
      formattedDate,                // YYYYMMDD
      String(Number(formattedDate)),  // Remove leading zeros if present
      date.format('YYYY-MM-DD')      // YYYY-MM-DD
    ];
    
    let stats = null;
    // Try each format until we find a match
    for (const dateKey of potentialKeys) {
      if (datePerformance[dateKey]) {
        stats = datePerformance[dateKey];
        break;
      }
    }
    
    if (!stats) {
      return { percent: 0, total: 0 };
    }
    
    // If ALL is selected, use the overall stats
    if (selectedStopTypes.includes('ALL')) {
      return {
        percent: stats.trackingPercent,
        total: stats.totalCount,
        tracked: stats.trackedCount,
        untracked: stats.untrackedCount
      };
    }
    
    // Otherwise, combine the selected stop types
    let totalTracked = 0;
    let totalStops = 0;
    
    if (selectedStopTypes.includes('F')) {
      totalTracked += stats.firstStops.tracked;
      totalStops += stats.firstStops.total;
    }
    
    if (selectedStopTypes.includes('M')) {
      totalTracked += stats.midStops.tracked;
      totalStops += stats.midStops.total;
    }
    
    if (selectedStopTypes.includes('L')) {
      totalTracked += stats.lastStops.tracked;
      totalStops += stats.lastStops.total;
    }
    
    if (totalStops === 0) {
      return { 
        percent: 0, 
        total: 0, 
        tracked: 0, 
        untracked: 0
      };
    }
    
    return {
      percent: Math.round((totalTracked / totalStops) * 100),
      total: totalStops,
      tracked: totalTracked,
      untracked: totalStops - totalTracked
    };
  };
  
  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
  };
  
  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.clone().add(1, 'month'));
  };
  
  // Select a date
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    
    // Generate tracking insights for the selected date
    if (data && data.length > 0) {
      const dateStr = date.format('YYYYMMDD');
      // Filter data for this specific date
      const dateData = data.filter(record => 
        String(record.DATE_ID) === dateStr ||
        record.dateStr === dateStr
      );
      
      // Get tracking insights for the date's data
      const insights = generateTrackingInsights(dateStr, dateData);
      setDailyInsights(insights);
    }
  };
  
  // Today button handler
  const handleTodayClick = () => {
    const today = dayjs();
    setSelectedDate(today);
    setCurrentMonth(today);
  };
  
  // Format date for display
  const formatDateDisplay = (date) => {
    return {
      day: date.format('ddd'),
      date: date.format('D'),
      month: date.format('MMM')
    };
  };
  
  // Check if a date is selected
  const isDateSelected = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Do the same for selected date
    const selectedDateFormats = [
      selectedDate.format('YYYYMMDD'),
      String(Number(selectedDate.format('YYYYMMDD'))),
      selectedDate.format('YYYY-MM-DD')
    ];
    
    // Check if any formats match
    return dateFormats.some(format => selectedDateFormats.includes(format));
  };
  
  // Check if a date is today
  const isToday = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Do the same for today
    const today = dayjs();
    const todayFormats = [
      today.format('YYYYMMDD'),
      String(Number(today.format('YYYYMMDD'))),
      today.format('YYYY-MM-DD')
    ];
    
    // Check if any formats match
    return dateFormats.some(format => todayFormats.includes(format));
  };
  
  // Helper function to get color based on tracking percent value
  const getColorByPercent = (percent) => {
    // Green for good tracking coverage, orange for moderate, red for poor
    return percent >= 90 ? '#52c41a' : // green
           percent >= 70 ? '#faad14' : // orange/amber 
           '#f5222d';                  // red
  };
  
  // Calculate radius and thickness for the radial chart (for multiple rings)
  const calculateRadialConfig = (stopType) => {
    // When "ALL" is selected, show a single large ring
    if (selectedStopTypes.includes('ALL')) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    // Count active stop types
    const activeTypes = selectedStopTypes.length;
    
    if (activeTypes === 1) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    if (stopType === 'F') {
      return { strokeWidth: 10, radius: 80 };
    } else if (stopType === 'M') {
      return { strokeWidth: 10, radius: 60 };
    } else if (stopType === 'L') {
      return { strokeWidth: 10, radius: activeTypes === 3 ? 40 : 60 };
    }
    
    return { strokeWidth: 10, radius: 80 };
  };

  // Helper function to get tracking data for a date using multiple format checks
  const getTrackingDataForDate = (date) => {
    // Get all possible formatted strings for this date
    const dateFormats = [
      date.format('YYYYMMDD'),                 // YYYYMMDD
      String(Number(date.format('YYYYMMDD'))), // Remove leading zeros
      date.format('YYYY-MM-DD')                // YYYY-MM-DD
    ];
    
    // Try each format to find a match in datePerformance
    for (const format of dateFormats) {
      if (datePerformance[format]) {
        return datePerformance[format];
      }
    }
    
    return null;
  };

  // Render daily insights
  const renderDailyInsights = () => {
    if (!dailyInsights || (!dailyInsights.insight && !dailyInsights.recommendation)) {
      return (
        <Card 
          title={
            <Tooltip title="AI-generated insights and recommendations for improving tracking coverage for this date">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EyeOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <span>Tracking Insights for {selectedDate.format('DD MMM YYYY')}</span>
              </div>
            </Tooltip>
          }
          style={{ marginTop: 16 }}
        >
          <Empty description="Excellent tracking coverage - no specific recommendations needed" />
        </Card>
      );
    }
    
    // Get priority color
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'critical': return 'red';
        case 'low': return 'orange';
        case 'medium': return 'blue';
        case 'high': return 'green';
        case 'maintenance': return 'cyan';
        default: return 'blue';
      }
    };
    
    const getPriorityIcon = (priority) => {
      switch (priority) {
        case 'critical': return '🚨';
        case 'low': return '⚠️';
        case 'medium': return 'ℹ️';
        case 'high': return '✅';
        case 'maintenance': return '🔧';
        default: return 'ℹ️';
      }
    };
    
    return (
      <Card 
        title={
          <Tooltip title="AI-generated insights and recommendations for improving tracking coverage on this date">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <EyeOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <span>Tracking Coverage Analysis for {selectedDate.format('DD MMM YYYY')}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {dailyInsights.priority && (
                  <Tag color={getPriorityColor(dailyInsights.priority)}>
                    {getPriorityIcon(dailyInsights.priority)} {dailyInsights.priority.toUpperCase()}
                  </Tag>
                )}
                {dailyInsights.category && (
                  <Tag color="green">
                    {dailyInsights.category.charAt(0).toUpperCase() + dailyInsights.category.slice(1)}
                  </Tag>
                )}
              </div>
            </div>
          </Tooltip>
        }
        style={{ marginTop: 16 }}
      >
        <Row gutter={[16, 16]}>
          {/* Key Metrics Summary */}
          {dailyInsights.metrics && (
            <Col span={24}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Statistic
                    title="Total Stops"
                    value={dailyInsights.metrics.totalStops}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tracked"
                    value={dailyInsights.metrics.trackedStops}
                    valueStyle={{ color: '#52c41a', fontSize: 18 }}
                    suffix={`(${dailyInsights.metrics.trackingPercent}%)`}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Untracked"
                    value={dailyInsights.metrics.untrackedStops}
                    valueStyle={{ color: '#f5222d', fontSize: 18 }}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Data Types</div>
                    <div style={{ fontSize: 14 }}>
                      <span style={{ color: '#1890ff' }}>Dep: {dailyInsights.metrics.departureDataCount}</span>
                      <br />
                      <span style={{ color: '#722ed1' }}>Arr: {dailyInsights.metrics.arrivalDataCount}</span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
            </Col>
          )}
          
          {/* Insight */}
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoCircleOutlined style={{ marginRight: 8, marginTop: 4, color: '#1890ff' }} />
              <div>
                <Text strong style={{ color: '#1890ff' }}>Analysis:</Text>
                <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>{dailyInsights.insight}</Paragraph>
              </div>
            </div>
          </Col>
          
          {/* Recommendation */}
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <BulbOutlined style={{ marginRight: 8, marginTop: 4, color: '#faad14' }} />
              <div>
                <Text strong style={{ color: '#faad14' }}>Recommendation:</Text>
                <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>{dailyInsights.recommendation}</Paragraph>
              </div>
            </div>
          </Col>
          
          {/* Stop Type Breakdown */}
          {dailyInsights.metrics && dailyInsights.metrics.stopTypeBreakdown && (
            <Col span={24}>
              <Divider style={{ margin: '12px 0' }} />
              <Text strong style={{ marginBottom: 8, display: 'block' }}>Stop Type Performance:</Text>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>First Stops</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.first.total > 0 ? 
                        Math.round((dailyInsights.metrics.stopTypeBreakdown.first.tracked / dailyInsights.metrics.stopTypeBreakdown.first.total) * 100) : 0}%
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.first.tracked}/{dailyInsights.metrics.stopTypeBreakdown.first.total}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Mid Stops</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.mid.total > 0 ? 
                        Math.round((dailyInsights.metrics.stopTypeBreakdown.mid.tracked / dailyInsights.metrics.stopTypeBreakdown.mid.total) * 100) : 0}%
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.mid.tracked}/{dailyInsights.metrics.stopTypeBreakdown.mid.total}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Last Stops</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.last.total > 0 ? 
                        Math.round((dailyInsights.metrics.stopTypeBreakdown.last.tracked / dailyInsights.metrics.stopTypeBreakdown.last.total) * 100) : 0}%
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {dailyInsights.metrics.stopTypeBreakdown.last.tracked}/{dailyInsights.metrics.stopTypeBreakdown.last.total}
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          )}
          
          {/* Improvement Potential */}
          {dailyInsights.improvement > 0 && (
            <Col span={24}>
              <Divider style={{ margin: '12px 0' }} />
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>Potential Coverage Improvement:</Text>
                </Col>
                <Col>
                  <Statistic 
                    value={dailyInsights.improvement} 
                    suffix="%" 
                    valueStyle={{ 
                      color: dailyInsights.improvement >= 20 ? '#52c41a' : 
                             dailyInsights.improvement >= 10 ? '#faad14' : '#1890ff',
                      fontSize: 20
                    }}
                    prefix={<ArrowUpOutlined />}
                  />
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={handlePrevMonth} 
        />
        <Space>
          <Text strong style={{ fontSize: 16 }}>{currentMonth.format('MMMM YYYY')}</Text>
          <Button type="link" size="small" onClick={handleTodayClick}>Today</Button>
        </Space>
        <Button 
          type="text" 
          icon={<RightOutlined />} 
          onClick={handleNextMonth} 
        />
      </div>
      
      {/* Date carousel with radial charts */}
      <div style={{ 
        overflowX: 'auto', 
        whiteSpace: 'nowrap', 
        padding: '8px 0 24px 0',
        marginBottom: '20px' 
      }}>
        <div style={{ display: 'inline-flex' }}>
          {visibleDates.map((date, index) => {
            const dateStr = date.format('YYYYMMDD');
            const isDateToday = isToday(date);
            const isSelected = isDateSelected(date);
            const dateDisplay = formatDateDisplay(date);
            
            // Get tracking data directly using the helper function
            const perfData = getTrackingDataForDate(date);
            const tracking = calculateTrackingForDate(date);
            const hasData = perfData !== null && tracking.total > 0;
            
            return (
              <div 
                key={index}
                onClick={() => handleDateSelect(date)}
                style={{ 
                  padding: '0 8px',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div 
                  style={{ 
                    border: isSelected ? '2px solid #1890ff' : 'none',
                    borderRadius: '8px',
                    padding: '8px 4px',
                    backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.1)' : isDateToday ? 'rgba(240, 242, 245, 0.5)' : 'transparent'
                  }}
                >
                  <div style={{ color: isSelected ? '#1890ff' : '#666', fontSize: 12, marginBottom: 4 }}>
                    {dateDisplay.day}
                  </div>
                  
                  {/* Radial progress chart */}
                  <div style={{ margin: '8px 0', height: 80, width: 80, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {hasData ? (
                      <>
                        {/* Show appropriate rings based on selected stop types */}
                        {(selectedStopTypes.includes('ALL') || selectedStopTypes.includes('F')) && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('F').radius) / 2, 
                              left: (80 - calculateRadialConfig('F').radius) / 2, 
                              width: calculateRadialConfig('F').radius, 
                              height: calculateRadialConfig('F').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={selectedStopTypes.includes('ALL') ? 
                                tracking.percent : 
                                perfData && perfData.firstStops && perfData.firstStops.total > 0 ?
                                Math.round((perfData.firstStops.tracked / perfData.firstStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(selectedStopTypes.includes('ALL') ? 
                                tracking.percent : 
                                perfData && perfData.firstStops && perfData.firstStops.total > 0 ?
                                Math.round((perfData.firstStops.tracked / perfData.firstStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('F').strokeWidth}
                              width={calculateRadialConfig('F').radius}
                            />
                          </div>
                        )}
                        
                        {selectedStopTypes.includes('M') && !selectedStopTypes.includes('ALL') && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('M').radius) / 2, 
                              left: (80 - calculateRadialConfig('M').radius) / 2, 
                              width: calculateRadialConfig('M').radius, 
                              height: calculateRadialConfig('M').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={perfData && perfData.midStops && perfData.midStops.total > 0 ?
                                Math.round((perfData.midStops.tracked / perfData.midStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(perfData && perfData.midStops && perfData.midStops.total > 0 ?
                                Math.round((perfData.midStops.tracked / perfData.midStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('M').strokeWidth}
                              width={calculateRadialConfig('M').radius}
                            />
                          </div>
                        )}
                        
                        {selectedStopTypes.includes('L') && !selectedStopTypes.includes('ALL') && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadialConfig('L').radius) / 2, 
                              left: (80 - calculateRadialConfig('L').radius) / 2, 
                              width: calculateRadialConfig('L').radius, 
                              height: calculateRadialConfig('L').radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={perfData && perfData.lastStops && perfData.lastStops.total > 0 ?
                                Math.round((perfData.lastStops.tracked / perfData.lastStops.total) * 100) : 0}
                              format={() => ''}
                              strokeColor={getColorByPercent(perfData && perfData.lastStops && perfData.lastStops.total > 0 ?
                                Math.round((perfData.lastStops.tracked / perfData.lastStops.total) * 100) : 0)}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadialConfig('L').strokeWidth}
                              width={calculateRadialConfig('L').radius}
                            />
                          </div>
                        )}
                        
                        {/* Overall percentage in the center */}
                        <div style={{ 
                          position: 'absolute', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: 16, 
                          fontWeight: 'bold', 
                          color: '#000',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: 'inset 0 0 3px rgba(0,0,0,0.1)'
                        }}>
                          {tracking.percent}%
                        </div>
                      </>
                    ) : (
                      <div style={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>No data</Text>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: isSelected || isDateToday ? 'bold' : 'normal',
                    color: isSelected ? '#1890ff' : isDateToday ? '#1890ff' : '#000'
                  }}>
                    {dateDisplay.date}
                  </div>
                  
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {dateDisplay.month}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Selected date details */}
      {selectedDate && datePerformance[selectedDate.format('YYYYMMDD')] && (
        <Card 
          size="small"
          title={
            <Text style={{ fontSize: 16 }}>
              {selectedDate.format('dddd, MMMM D, YYYY')} Tracking Coverage Details
              <span style={{ fontSize: 12, color: '#888', marginLeft: 10 }}>
                (Date ID: {selectedDate.format('YYYYMMDD')})
              </span>
            </Text>
          }
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of stops that have actual timing data available across all selected stop types">
                    <Space>
                      <span>Overall Coverage</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={calculateTrackingForDate(selectedDate).percent}
                suffix="%"
                valueStyle={{ color: getColorByPercent(calculateTrackingForDate(selectedDate).percent) }}
              />
              <Text type="secondary">{calculateTrackingForDate(selectedDate).total} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of first stops that have actual timing data available">
                    <Space>
                      <span>First Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.tracked / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.tracked / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.firstStops.total || 0} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of mid stops that have actual timing data available">
                    <Space>
                      <span>Mid Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.tracked / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.tracked / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.midStops.total || 0} records</Text>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title={
                  <Tooltip title="Percentage of last stops that have actual timing data available">
                    <Space>
                      <span>Last Stops</span>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Space>
                  </Tooltip>
                }
                value={
                  datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total > 0 ? 
                  Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.tracked / 
                  datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total) * 100) : 0
                }
                suffix="%"
                valueStyle={{ 
                  color: getColorByPercent(
                    datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total > 0 ? 
                    Math.round((datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.tracked / 
                    datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total) * 100) : 0
                  )
                }}
              />
              <Text type="secondary">{datePerformance[selectedDate.format('YYYYMMDD')]?.lastStops.total || 0} records</Text>
            </Col>
          </Row>
        </Card>
      )}
      
      {/* Tracking insights for selected date */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {renderDailyInsights()}
        </Col>
      </Row>
      
      {Object.keys(datePerformance).length === 0 && (
        <Empty 
          description="No tracking data available for the selected period" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </div>
  );
};

export default TrackedDailyOverviewTab; 